import React, { useState, useEffect } from 'react';

const EQUATION_TYPES = {
  linear: {
    label: 'Linear',
    template: 'y = {m}x + {b}',
    params: [
      { key: 'm', label: 'm', default: '1' },
      { key: 'b', label: 'b', default: '0' }
    ],
    build: (params) => `y=${params.m}x${params.b >= 0 ? '+' : ''}${params.b}`
  },
  quadratic: {
    label: 'Quadratic',
    template: 'y = {a}x² + {b}x + {c}',
    params: [
      { key: 'a', label: 'a', default: '1' },
      { key: 'b', label: 'b', default: '0' },
      { key: 'c', label: 'c', default: '0' }
    ],
    build: (params) => {
      let eq = `y=${params.a}x^2`;
      if (params.b !== '0' && params.b !== 0) eq += `${params.b >= 0 ? '+' : ''}${params.b}x`;
      if (params.c !== '0' && params.c !== 0) eq += `${params.c >= 0 ? '+' : ''}${params.c}`;
      return eq;
    }
  },
  circle: {
    label: 'Circle',
    template: 'x² + y² = {r}²',
    params: [
      { key: 'r', label: 'r', default: '5' }
    ],
    build: (params) => `x^2+y^2=${params.r}^2`
  },
  sine: {
    label: 'Sine Wave',
    template: 'y = {a}sin({b}x)',
    params: [
      { key: 'a', label: 'a', default: '1' },
      { key: 'b', label: 'b', default: '1' }
    ],
    build: (params) => `y=${params.a}sin(${params.b}x)`
  },
  cosine: {
    label: 'Cosine Wave',
    template: 'y = {a}cos({b}x)',
    params: [
      { key: 'a', label: 'a', default: '1' },
      { key: 'b', label: 'b', default: '1' }
    ],
    build: (params) => `y=${params.a}cos(${params.b}x)`
  },
  exponential: {
    label: 'Exponential',
    template: 'y = {a} · {b}^x',
    params: [
      { key: 'a', label: 'a', default: '1' },
      { key: 'b', label: 'base', default: 'e' }
    ],
    build: (params) => params.b === 'e' ? `y=${params.a}e^x` : `y=${params.a}*${params.b}^x`
  },
  logarithm: {
    label: 'Logarithm',
    template: 'y = log_{b}(x)',
    params: [
      { key: 'b', label: 'base', default: 'e' }
    ],
    build: (params) => params.b === 'e' ? 'y=ln(x)' : params.b === '10' ? 'y=log(x)' : `y=log_${params.b}(x)`
  },
  absolute: {
    label: 'Absolute Value',
    template: 'y = |{a}x + {b}|',
    params: [
      { key: 'a', label: 'a', default: '1' },
      { key: 'b', label: 'b', default: '0' }
    ],
    build: (params) => `y=|${params.a}x${params.b >= 0 ? '+' : ''}${params.b}|`
  }
};

const EquationBuilder = ({ onInsert }) => {
  const [selectedType, setSelectedType] = useState('linear');
  const [params, setParams] = useState({});
  const [preview, setPreview] = useState('');

  // Initialize params when type changes
  useEffect(() => {
    const type = EQUATION_TYPES[selectedType];
    const newParams = {};
    type.params.forEach(p => {
      newParams[p.key] = p.default;
    });
    setParams(newParams);
  }, [selectedType]);

  // Update preview when params change
  useEffect(() => {
    const type = EQUATION_TYPES[selectedType];
    if (type && Object.keys(params).length > 0) {
      try {
        setPreview(type.build(params));
      } catch (e) {
        setPreview('');
      }
    }
  }, [selectedType, params]);

  const handleParamChange = (key, value) => {
    setParams(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleInsert = () => {
    if (preview && onInsert) {
      onInsert(preview);
    }
  };

  const currentType = EQUATION_TYPES[selectedType];

  return (
    <div className="wb-equation-builder">
      <div className="wb-equation-builder-row">
        <span className="wb-equation-builder-label">Type:</span>
        <select
          className="wb-equation-builder-select"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          {Object.entries(EQUATION_TYPES).map(([key, type]) => (
            <option key={key} value={key}>{type.label}</option>
          ))}
        </select>
      </div>

      <div className="wb-equation-builder-row">
        <span className="wb-equation-builder-label">Params:</span>
        {currentType.params.map(param => (
          <React.Fragment key={param.key}>
            <span style={{ fontSize: '12px', color: '#6b7280' }}>{param.label}=</span>
            <input
              type="text"
              className="wb-equation-builder-input"
              value={params[param.key] || ''}
              onChange={(e) => handleParamChange(param.key, e.target.value)}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </React.Fragment>
        ))}
      </div>

      <div className="wb-equation-builder-row">
        <span className="wb-equation-builder-label">Result:</span>
        <span className="wb-equation-builder-preview">{preview}</span>
        <button
          className="wb-equation-builder-insert"
          onClick={handleInsert}
        >
          Insert
        </button>
      </div>
    </div>
  );
};

export default EquationBuilder;
